import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { useUser } from '../context/UserContext';
import { formatCurrency } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import moment from 'moment-timezone';
import {
  isPushSupported,
  isNotificationsPermitted,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications
} from '../services/notifications';
import axiosInstance from '../utils/axiosConfig';

// List of common timezones
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
  'Pacific/Auckland'
];

const SettingsPage = () => {
  const { profile, updateProfile, updateTimezone, loading, error, setError, browserTimezone } = useUser();
  const { currentUser, logout } = useAuth();
  
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [timezone, setTimezone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timezoneChanged, setTimezoneChanged] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  
  // Load user profile data when component mounts or profile changes
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      
      if (profile.currentMonthlyLimit) {
        setMonthlyLimit(profile.currentMonthlyLimit.limit.toString());
      }
      
      setTimezone(profile.timezone || browserTimezone || 'UTC');
    } else {
      // Default to browser timezone if no profile
      setTimezone(browserTimezone || 'UTC');
    }
  }, [profile, browserTimezone]);
  
  // Check notification support and permission status on mount
  useEffect(() => {
    const supported = isPushSupported();
    setNotificationsSupported(supported);
    
    if (supported) {
      setNotificationsEnabled(isNotificationsPermitted());
    }
  }, []);
  
  const handleSaveSettings = async () => {
    try {
      setSaveSuccess(false);
      
      if (!monthlyLimit.trim() || !displayName.trim()) {
        setError('Monthly limit and display name are required');
        return;
      }
      
      const limitValue = parseFloat(monthlyLimit);
      if (isNaN(limitValue) || limitValue < 0) {
        setError('Please enter a valid monthly limit (greater than or equal to 0)');
        return;
      }
      
      await updateProfile({
        displayName: displayName.trim(),
        monthlyLimit: limitValue,
        timezone
      });
      
      setTimezoneChanged(false);
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    }
  };
  
  const handleSaveTimezone = async () => {
    try {
      setSaveSuccess(false);
      
      await updateTimezone(timezone);
      
      setTimezoneChanged(false);
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to update timezone');
    }
  };
  
  const handleTimezoneChange = (event) => {
    const newTimezone = event.target.value;
    setTimezone(newTimezone);
    setTimezoneChanged(newTimezone !== profile?.timezone);
  };
  
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleNotificationToggle = async (event) => {
    console.log('Notification toggle clicked');
    console.log('Event:', event);
    console.log('Checked state:', event.target.checked);
    console.log('Notifications supported:', notificationsSupported);
    
    if (!notificationsSupported) {
      console.log('Notifications not supported');
      setError('Notifications are not supported on this device/browser');
      return;
    }
    
    const enabled = event.target.checked;
    console.log('Setting notifications to:', enabled);
    
    // Immediately update the UI state
    setNotificationsEnabled(enabled);
    
    if (enabled) {
      try {
        console.log('Requesting notification permission...');
        const permitted = await requestNotificationPermission();
        console.log('Permission result:', permitted);
        
        if (!permitted) {
          console.log('Permission denied');
          setError('Notifications are blocked. Please enable them in your browser settings.');
          setNotificationsEnabled(false);
          return;
        }

        console.log('Subscribing to push notifications...');
        const subscribed = await subscribeToPushNotifications();
        console.log('Subscription result:', subscribed);
        
        if (subscribed) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        } else {
          setError('Failed to enable notifications. Please try again.');
          setNotificationsEnabled(false);
        }
      } catch (err) {
        console.error('Error enabling notifications:', err);
        setError('Failed to enable notifications. Please try again.');
        setNotificationsEnabled(false);
      }
    } else {
      try {
        console.log('Unsubscribing from push notifications...');
        const unsubscribed = await unsubscribeFromPushNotifications();
        console.log('Unsubscribe result:', unsubscribed);
        
        if (!unsubscribed) {
          setError('Failed to disable notifications. Please try again.');
          setNotificationsEnabled(true); // Revert if unsubscribe failed
        }
      } catch (err) {
        console.error('Error disabling notifications:', err);
        setError('Failed to disable notifications. Please try again.');
        setNotificationsEnabled(true); // Revert if unsubscribe failed
      }
    }
  };
  
  const handleTestNotification = async () => {
    try {
      console.log('Sending test notification request...');
      const response = await axiosInstance.post('/push/test');
      console.log('Test notification response:', response.data);
      
      // Show a notification-specific success message
      setSaveSuccess('Test notification sent successfully!');
      setTimeout(() => setSaveSuccess(''), 3000);
      
      // Also show a local notification for immediate feedback
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Test Notification', {
          body: 'This is a test notification from Spending Tracker',
          icon: '/apple-touch-icon.png'
        });
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send test notification';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000);
    }
  };
  
  // Helper function to get a friendly timezone name
  const getTimezoneName = (tz) => {
    try {
      return `${tz} (${moment.tz(tz).format('z')})`; 
    } catch (e) {
      return tz;
    }
  };
  
  // Get current time in selected timezone
  const getCurrentTimeInSelectedTimezone = () => {
    try {
      return moment().tz(timezone).format('h:mm A (ddd, MMM D, YYYY)');
    } catch (e) {
      return 'Invalid timezone';
    }
  };
  
  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
          Settings
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}
      
      {/* Timezone Settings */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Timezone Settings
        </Typography>
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="timezone-label">Your Timezone</InputLabel>
          <Select
            labelId="timezone-label"
            value={timezone}
            onChange={handleTimezoneChange}
            label="Your Timezone"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {getTimezoneName(tz)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Current time in selected timezone: {getCurrentTimeInSelectedTimezone()}
          </FormHelperText>
        </FormControl>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Setting the correct timezone ensures your transactions are recorded with the right date and time,
            and your daily budget calculations are accurate.
          </Typography>
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveTimezone}
            disabled={loading || !timezoneChanged}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Timezone'}
          </Button>
        </Box>
      </Paper>
      
      {/* User Profile Settings */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          User Profile
        </Typography>
        
        <TextField
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          margin="normal"
        />
        
        <TextField
          label="Email"
          value={currentUser?.email || ''}
          fullWidth
          margin="normal"
          disabled
        />
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Profile'}
          </Button>
        </Box>
      </Paper>
      
      {/* Budget Settings */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Budget Settings
        </Typography>
        
        <TextField
          label="Monthly Spending Limit"
          value={monthlyLimit}
          onChange={(e) => setMonthlyLimit(e.target.value)}
          type="number"
          fullWidth
          margin="normal"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
        />
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Your monthly limit will be used to calculate your daily spending goals.
          </Typography>
        </Box>
        
        {profile?.currentMonthlyLimit && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Current Budget
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Monthly Limit" 
                  secondary={formatCurrency(profile.currentMonthlyLimit.limit)}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Start Date" 
                  secondary={
                    profile.timezone 
                      ? moment(profile.currentMonthlyLimit.startDate).tz(profile.timezone).format('MMMM D, YYYY')
                      : moment(profile.currentMonthlyLimit.startDate).format('MMMM D, YYYY')
                  }
                />
              </ListItem>
            </List>
          </Box>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Budget'}
          </Button>
        </Box>
      </Paper>
      
      {/* Account Settings */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Settings
        </Typography>
        
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={logout}
            color="primary"
          >
            Logout
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleOpenDeleteDialog}
            color="error"
          >
            Delete Account
          </Button>
        </Box>
      </Paper>
      
      {/* Notification Settings */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Settings
        </Typography>
        
        <FormControlLabel
          control={
            <Tooltip 
              title={!notificationsSupported ? "Notifications are not supported on this device/browser" : ""}
              placement="right"
            >
              <span>
                <Switch
                  checked={notificationsEnabled}
                  onChange={handleNotificationToggle}
                  disabled={!notificationsSupported}
                />
              </span>
            </Tooltip>
          }
          label="Enable Push Notifications"
        />
        
        {notificationsEnabled && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleTestNotification}
              disabled={!notificationsEnabled}
            >
              Send Test Notification
            </Button>
          </Box>
        )}

        {!notificationsSupported && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Notifications are not supported on this device/browser. Please use a supported browser or install the app.
          </Typography>
        )}
      </Paper>
      
      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCloseDeleteDialog} color="error">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;